/**
 * DEV-only procedural one-shots (short envelope tones / noise).
 * Production builds must never call these for shipped gameplay —
 * gate with isViteDev() / allowDevSynthFallback() in the engine.
 */

import type { ClipId } from "./catalog";

export type SynthKind = "tone" | "noise" | "chord" | "thump";

export interface SynthSpec {
  kind: SynthKind;
  /** Primary frequency (Hz) for tone/chord/thump */
  freq: number;
  /** Duration seconds (clamped ~0.03–0.25 for one-shots) */
  duration: number;
  /** Peak amplitude 0–1 */
  gain: number;
  /** Optional second partial for chord */
  freq2?: number;
  /** Exponential decay rate (higher = shorter tail) */
  decay?: number;
}

/** Sensible defaults per catalog id — restrained, not harsh. */
const SPECS: Record<string, SynthSpec> = {
  "sfx.answer.meet": {
    kind: "chord",
    freq: 440,
    freq2: 660,
    duration: 0.18,
    gain: 0.22,
    decay: 10,
  },
  "sfx.answer.weak": {
    kind: "tone",
    freq: 220,
    duration: 0.12,
    gain: 0.16,
    decay: 14,
  },
  "sfx.answer.miss": {
    kind: "tone",
    freq: 110,
    duration: 0.1,
    gain: 0.12,
    decay: 18,
  },
  "sfx.answer.blind-hit": {
    kind: "tone",
    freq: 280,
    duration: 0.12,
    gain: 0.14,
    decay: 14,
  },
  "sfx.answer.target": {
    kind: "tone",
    freq: 880,
    duration: 0.06,
    gain: 0.1,
    decay: 28,
  },
  "sfx.still": {
    kind: "tone",
    freq: 196,
    duration: 0.16,
    gain: 0.14,
    decay: 9,
  },
  "sfx.silence": {
    kind: "chord",
    freq: 330,
    freq2: 495,
    duration: 0.22,
    gain: 0.12,
    decay: 7,
  },
  "sfx.breach": {
    kind: "noise",
    freq: 100,
    duration: 0.14,
    gain: 0.18,
    decay: 16,
  },
  "sfx.press": {
    kind: "noise",
    freq: 80,
    duration: 0.08,
    gain: 0.1,
    decay: 22,
  },
  "sfx.overflow": {
    kind: "thump",
    freq: 70,
    duration: 0.12,
    gain: 0.2,
    decay: 12,
  },
  "sfx.spawn-fear": {
    kind: "noise",
    freq: 400,
    duration: 0.15,
    gain: 0.12,
    decay: 14,
  },
  "sfx.spawn-despair": {
    kind: "tone",
    freq: 90,
    duration: 0.18,
    gain: 0.14,
    decay: 10,
  },
  "sfx.spawn-generic": {
    kind: "noise",
    freq: 200,
    duration: 0.1,
    gain: 0.1,
    decay: 16,
  },
  "sfx.multiply": {
    kind: "tone",
    freq: 320,
    duration: 0.1,
    gain: 0.12,
    decay: 16,
  },
  "sfx.redeem": {
    kind: "chord",
    freq: 392,
    freq2: 588,
    duration: 0.22,
    gain: 0.18,
    decay: 8,
  },
  "sfx.abide": {
    kind: "tone",
    freq: 262,
    duration: 0.2,
    gain: 0.12,
    decay: 6,
  },
  "sfx.layer": {
    kind: "tone",
    freq: 300,
    duration: 0.1,
    gain: 0.12,
    decay: 14,
  },
  "sfx.restore": {
    kind: "tone",
    freq: 350,
    duration: 0.12,
    gain: 0.12,
    decay: 12,
  },
  "sfx.proclaim-start": {
    kind: "tone",
    freq: 240,
    duration: 0.14,
    gain: 0.14,
    decay: 10,
  },
  "sfx.proclaim-end": {
    kind: "tone",
    freq: 200,
    duration: 0.12,
    gain: 0.1,
    decay: 12,
  },
  "sfx.seal-faith": {
    kind: "chord",
    freq: 196,
    freq2: 294,
    duration: 0.2,
    gain: 0.1,
    decay: 8,
  },
  "sfx.seal-peace": {
    kind: "chord",
    freq: 220,
    freq2: 330,
    duration: 0.22,
    gain: 0.09,
    decay: 7,
  },
  "sfx.seal-generic": {
    kind: "tone",
    freq: 262,
    duration: 0.16,
    gain: 0.09,
    decay: 10,
  },
  "ui.click": {
    kind: "tone",
    freq: 600,
    duration: 0.04,
    gain: 0.08,
    decay: 40,
  },
  "ui.deny": {
    kind: "tone",
    freq: 140,
    duration: 0.08,
    gain: 0.1,
    decay: 20,
  },
  "ui.pause": {
    kind: "tone",
    freq: 400,
    duration: 0.06,
    gain: 0.08,
    decay: 25,
  },
  "ui.queue": {
    kind: "tone",
    freq: 480,
    duration: 0.05,
    gain: 0.08,
    decay: 30,
  },
  "ui.queue-cancel": {
    kind: "tone",
    freq: 360,
    duration: 0.05,
    gain: 0.07,
    decay: 30,
  },
  "ui.reveal": {
    kind: "tone",
    freq: 520,
    duration: 0.08,
    gain: 0.07,
    decay: 18,
  },
  "sting.start": {
    kind: "chord",
    freq: 262,
    freq2: 392,
    duration: 0.25,
    gain: 0.16,
    decay: 6,
  },
  "sting.win": {
    kind: "chord",
    freq: 330,
    freq2: 495,
    duration: 0.28,
    gain: 0.18,
    decay: 5,
  },
  "sting.lose": {
    kind: "tone",
    freq: 98,
    duration: 0.3,
    gain: 0.16,
    decay: 4,
  },
  "sting.near-miss": {
    kind: "chord",
    freq: 294,
    freq2: 370,
    duration: 0.26,
    gain: 0.15,
    decay: 6,
  },
  // Soft heart thump: long enough that the envelope reaches silence (no end-click)
  // Peak high enough that rest gain (~0.32) is faintly readable under the bed
  "state.pulse-thump": {
    kind: "thump",
    freq: 52,
    duration: 0.16,
    gain: 0.28,
    decay: 22,
  },
};

const DEFAULT_SPEC: SynthSpec = {
  kind: "tone",
  freq: 300,
  duration: 0.08,
  gain: 0.1,
  decay: 16,
};

export function synthSpecForClip(id: ClipId): SynthSpec {
  return SPECS[id] ?? DEFAULT_SPEC;
}

/**
 * Render a short mono buffer with a procedural envelope.
 * Safe to call only when DEV synth is allowed.
 * Always fades to silence at the end so one-shots never hard-cut (clicks).
 */
export function renderSynthBuffer(
  context: AudioContext,
  id: ClipId,
): AudioBuffer {
  const spec = synthSpecForClip(id);
  const duration = Math.max(0.03, Math.min(0.35, spec.duration));
  const sampleRate = context.sampleRate;
  const n = Math.max(1, Math.floor(sampleRate * duration));
  const buffer = context.createBuffer(1, n, sampleRate);
  const data = buffer.getChannelData(0);
  const decay = spec.decay ?? 12;
  const peak = Math.max(0, Math.min(0.4, spec.gain));
  const attack = Math.min(0.008, duration * 0.2);
  // Tail of buffer must reach ~0 — force linear silence in last 15% if needed
  const releaseStart = duration * 0.55;

  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const attackEnv = t < attack ? t / attack : 1;
    let env = attackEnv * Math.exp(-decay * t) * peak;
    if (t > releaseStart) {
      const u = (t - releaseStart) / (duration - releaseStart);
      env *= Math.max(0, 1 - u * u);
    }
    // Absolute zero on last few samples
    if (i >= n - 4) env = 0;

    let sample = 0;
    switch (spec.kind) {
      case "noise": {
        // Soft filtered-ish noise (still deterministic enough after envelope)
        const white = Math.random() * 2 - 1;
        sample = white * (0.5 + 0.5 * Math.sin(2 * Math.PI * spec.freq * t));
        break;
      }
      case "thump": {
        // Heart-like: sine with pitch drop, no noise component
        const u = t / duration;
        const f = spec.freq * (1.35 - 0.85 * u);
        sample = Math.sin(2 * Math.PI * f * t);
        // Soft low harmonic
        sample += 0.35 * Math.sin(2 * Math.PI * (f * 0.5) * t);
        sample *= 0.75;
        break;
      }
      case "chord": {
        const a = Math.sin(2 * Math.PI * spec.freq * t);
        const b = Math.sin(2 * Math.PI * (spec.freq2 ?? spec.freq * 1.5) * t);
        sample = (a + b) * 0.5;
        break;
      }
      case "tone":
      default:
        sample = Math.sin(2 * Math.PI * spec.freq * t);
        break;
    }
    data[i] = sample * env;
  }

  return buffer;
}

/**
 * Soft loop bed for state / music. Used only if DEV + missing loop files.
 * No random noise — white noise in a loop reads as continuous scratch.
 * Ends match starts (cosine edge) to avoid loop-point clicks.
 *
 * Music IDs get a short environmental sketch (forest / cliff / volcano).
 */
export function renderLoopBedBuffer(
  context: AudioContext,
  id: ClipId,
  seconds = 2,
): AudioBuffer {
  const sampleRate = context.sampleRate;
  const isMusic =
    id.startsWith("music.") ||
    id.startsWith("ambient.") ||
    id.includes("foundation") ||
    id.includes("pressure") ||
    id.includes("secret");

  if (isMusic) {
    return renderEnvMusicLoopBuffer(context, id);
  }

  // Prefer integer cycles of base freq so the loop joins cleanly
  const base =
    id.includes("lie") || id.includes("bleed")
      ? 64
      : id.includes("drone")
        ? 55
        : id.includes("truth")
          ? 110
          : id.includes("proclaim")
            ? 98
            : 72;
  // Snap duration so base Hz completes whole cycles
  const cycles = Math.max(4, Math.round(base * seconds));
  const duration = cycles / base;
  const n = Math.floor(sampleRate * duration);
  const buffer = context.createBuffer(1, n, sampleRate);
  const data = buffer.getChannelData(0);

  const isBleed = id.includes("bleed");
  const isLie = id.includes("lie");
  const gain = isBleed ? 0.035 : isLie ? 0.032 : 0.038;

  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    // Equal-power-ish crossfade at loop ends (both ends → 0)
    const edge = 0.04;
    let env = 1;
    if (t < edge) env = 0.5 - 0.5 * Math.cos((Math.PI * t) / edge);
    else if (t > duration - edge) {
      env = 0.5 - 0.5 * Math.cos((Math.PI * (duration - t)) / edge);
    }

    let s =
      Math.sin(2 * Math.PI * base * t) * 0.7 +
      Math.sin(2 * Math.PI * (base * 2) * t) * 0.18;
    // Gentle slow AM so beds feel alive without noise grain
    s *= 0.92 + 0.08 * Math.sin(2 * Math.PI * 0.35 * t);
    if (isBleed) {
      // Very soft grit via ring-mod, still periodic (no white noise)
      s *= 0.85 + 0.15 * Math.sin(2 * Math.PI * (base * 7) * t);
    }
    data[i] = s * gain * env;
  }
  // Exact zero at endpoints for seamless loop
  if (n > 0) {
    data[0] = 0;
    data[n - 1] = 0;
  }
  return buffer;
}

/** Compact DEV fallback for environmental music stems (bright forest / airy cliff / bass volcano). */
function renderEnvMusicLoopBuffer(
  context: AudioContext,
  id: ClipId,
): AudioBuffer {
  const sampleRate = context.sampleRate;
  const seconds = 4;
  const n = Math.floor(sampleRate * seconds);
  const buffer = context.createBuffer(1, n, sampleRate);
  const data = buffer.getChannelData(0);
  const isCliff = id.includes("ambient") || id.includes("secret");
  const isVolcano = id.includes("pressure");

  // Cheap deterministic noise
  let seed = isVolcano ? 0x701ca : isCliff ? 0xc11ff0 : 0x51a7e1;
  const noise = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    noise[i] = seed / 0x80000000 - 1;
  }

  // Simple one-pole filters
  let hp = 0;
  let prev = 0;
  let lp = 0;
  const hpA = 0.92; // rough high-pass-ish
  const lpA = isVolcano ? 0.08 : 0.25;

  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const edge = 0.25;
    let env = 1;
    if (t < edge) env = 0.5 - 0.5 * Math.cos((Math.PI * t) / edge);
    else if (t > seconds - edge) {
      env = 0.5 - 0.5 * Math.cos((Math.PI * (seconds - t)) / edge);
    }

    const x = noise[i]!;
    hp = hpA * (hp + x - prev);
    prev = x;
    lp += lpA * (x - lp);

    let s = 0;
    if (isVolcano) {
      s =
        Math.sin(2 * Math.PI * 40 * t) * 0.25 +
        lp * 0.4 +
        hp * 0.08 +
        Math.sin(2 * Math.PI * 55 * t) * 0.12;
      s *= 0.5;
    } else if (isCliff) {
      // Airy wind — mid noise only
      s = hp * 0.45 * (0.7 + 0.3 * Math.sin(2 * Math.PI * 0.12 * t));
      if (t > 1.2 && t < 2.2) {
        const u = (t - 1.2) / 1;
        s += Math.sin(2 * Math.PI * 1800 * t) * Math.sin(Math.PI * u) * 0.08;
      }
      s *= 0.55;
    } else {
      // Forest: soft mid water + gentle chirps (no HF plastic hiss)
      const flow = 0.85 + 0.15 * Math.sin(2 * Math.PI * 0.12 * t);
      s = lp * 0.22 * flow + hp * 0.06 * flow;
      const chirps = [
        [0.8, 0.28, 2200, 2800],
        [2.0, 0.22, 2600, 2300],
        [3.1, 0.3, 2000, 2500],
      ] as const;
      for (const [t0, dur, f0, f1] of chirps) {
        if (t >= t0 && t < t0 + dur) {
          const u = (t - t0) / dur;
          const envB =
            u < 0.12
              ? (u / 0.12) * (u / 0.12)
              : Math.exp(-((u - 0.12) / 0.88) * 2.8);
          const f = f0 + (f1 - f0) * u * u * (3 - 2 * u);
          s += Math.sin(2 * Math.PI * f * (t - t0) + f0) * envB * 0.2;
        }
      }
      s *= 0.5;
    }
    data[i] = Math.tanh(s * env * 0.9);
  }
  if (n > 0) {
    data[0] = 0;
    data[n - 1] = 0;
  }
  return buffer;
}
